import React from 'react';

import { AlertAnimation } from '@eika/alert-animation';
import { Button, LinkButton } from '@eika/button';
import { WarningCard } from '@eika/card';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { Icon, IconTypes } from '@eika/icon';

import type { ApprovalStatus } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import CenteredModalContent from '../shared/CenteredModalContent/CenteredModalContent';
import { ErrorCardWrapper } from '../shared/ErrorCardWrapper';

import './AvtaleGiro/CreateAvtaleGiroForm.scss';
import './AvtaleGiroResultInfo.scss';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

type AvtalegiroStatus = ApprovalStatus;

export interface AvtalegiroResultInfoProps {
    error?: RequestExceptionWithResponse;
    status?: AvtalegiroStatus;
    retry: () => void;
    onClose: () => void;
    onRetryManually?: () => void;
}

const AvtaleGiroResultInfo: React.FC<AvtalegiroResultInfoProps> = ({
    error,
    status,
    retry,
    onClose,
    onRetryManually,
}) => {
    const translated = useTranslation();
    const infoMessage = translated.avtalegiroValidation;

    if (error) {
        return (
            <CenteredModalContent>
                <ErrorCardWrapper exception={error} isModal>
                    <Button onClick={retry}>{translated.ok}</Button>
                </ErrorCardWrapper>
            </CenteredModalContent>
        );
    }

    switch (status) {
        case 'UNKNOWN':
            return (
                <CenteredModalContent withMessage>
                    <Icon className="wallet-questionmark" type={IconTypes.WALLET_QUESTIONMARK} />
                    <h3 className="typography-h3">{infoMessage.error.heading}</h3>
                    <p>{infoMessage.error.description}</p>
                    <Button onClick={onClose} size="small">
                        {infoMessage.error.check}
                    </Button>
                </CenteredModalContent>
            );
        case 'REJECTED':
            return (
                <CenteredModalContent>
                    <WarningCard
                        className="avtalegiro-result-info__warning-card"
                        title={infoMessage.rejected.heading}
                        isModal
                        icon={<AlertAnimation />}
                    >
                        <p>{infoMessage.rejected.description}</p>
                        <div className="avtalegiro-result-info__warning-rejected">
                            <LinkButton className="avtalegiro-result-info__warning-link-button" onClick={onClose}>
                                {translated.cancel}
                            </LinkButton>
                            <Button className="warning__button" onClick={onRetryManually}>
                                {infoMessage.rejected.tryAgain}
                            </Button>
                        </div>
                    </WarningCard>
                </CenteredModalContent>
            );
        default:
            return (
                <CenteredModalContent withMessage>
                    <CheckmarkAnimation
                        className="avtalegiro-success-checkmark"
                        checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
                    />
                    <h3 className="typography-h3">{infoMessage.createSuccess}</h3>
                </CenteredModalContent>
            );
    }
};

export default AvtaleGiroResultInfo;
